import React from "react";
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Grid,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { modalStyle, headerStyle, inputStyle } from "../OrderEntry/styles";
import { tokens, withAlpha } from "../../Common/designTokens";
import { FIELD_TYPES, SECURITY_FIELDS } from "./updateSecurityConfig";
import { useCreateSecurity } from "./useCreateSecurity";
import { FloatInput } from "../OrderEntry/NumericInputs";
import IssuerAutocomplete from "./IssuerAutocomplete";

// Mirror the pinned order from UpdateSecurityPopup
const PINNED_KEYS = ["identifier", "price", "poolFactor"];
const PINNED_FIELDS = PINNED_KEYS
  .map((key) => SECURITY_FIELDS.find((f) => f.key === key))
  .filter(Boolean);
const REMAINING_FIELDS = SECURITY_FIELDS.filter((f) => !PINNED_KEYS.includes(f.key));

const FIELD_LABEL_MAP = Object.fromEntries(SECURITY_FIELDS.map((f) => [f.key, f.label]));

const CreateSecurityPopup = ({ open, onClose, onCreated, fieldOptions = {} }) => {
  const {
    identifierValue,
    identifierChecked,
    identifierCheckLoading,
    identifierCheckError,
    formValues,
    errors,
    submitError,
    loading,
    handleIdentifierChange,
    handleChange,
    handleSubmit,
  } = useCreateSecurity({ open, onClose, onCreated });

  const fieldDisabled = !identifierChecked;

  const identifierAdornment = identifierCheckLoading ? (
    <InputAdornment position="end">
      <CircularProgress size={18} color="inherit" />
    </InputAdornment>
  ) : null;

  const renderIdentifierField = () => (
    <Grid item xs={2.4} key="identifier">
      <TextField
        fullWidth
        variant="filled"
        label="Identifier"
        name="identifier"
        value={identifierValue}
        onChange={(e) => handleIdentifierChange(e.target.value)}
        error={Boolean(errors.identifier) || Boolean(identifierCheckError)}
        sx={inputStyle}
        InputProps={{
          disableUnderline: true,
          endAdornment: identifierAdornment,
        }}
        inputProps={{ maxLength: 50 }}
      />
    </Grid>
  );

  const renderSelectField = (field) => {
    const options = fieldOptions[field.optionsKey] || [];
    const currentVal = formValues[field.key] ?? "";
    return (
      <Grid item xs={2.4} key={field.key}>
        <FormControl
          fullWidth
          variant="filled"
          error={Boolean(errors[field.key])}
          disabled={fieldDisabled}
          sx={inputStyle}
        >
          <InputLabel>{field.label}</InputLabel>
          <Select
            name={field.key}
            value={currentVal}
            onChange={(e) => handleChange(field.key, e.target.value)}
            disableUnderline
            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
          >
            <MenuItem value=""><em>Select</em></MenuItem>
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    );
  };

  const renderDateField = (field) => {
    const stored = formValues[field.key] ?? "";
    const dayjsValue = stored ? dayjs(stored, "YYYY-MM-DD") : null;
    return (
      <Grid item xs={2.4} key={field.key}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={field.label}
            value={dayjsValue}
            disabled={fieldDisabled}
            onChange={(newVal) => {
              if (!newVal || !newVal.isValid()) {
                handleChange(field.key, "");
                return;
              }
              handleChange(field.key, newVal.format("YYYY-MM-DD"));
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "filled",
                error: Boolean(errors[field.key]),
                sx: inputStyle,
                InputProps: { disableUnderline: true },
              },
            }}
          />
        </LocalizationProvider>
      </Grid>
    );
  };

  const renderIssuerField = (field) => (
    <Grid item xs={2.4} key={field.key}>
      <IssuerAutocomplete
        label={field.label}
        value={formValues[field.key] ?? ""}
        onChange={(val) => handleChange(field.key, val)}
        options={fieldOptions[field.optionsKey] || []}
        error={Boolean(errors[field.key])}
        disabled={fieldDisabled}
      />
    </Grid>
  );

  const renderField = (field) => {
    if (field.key === "identifier") return renderIdentifierField();
    if (field.key === "issuer") return renderIssuerField(field);
    if (field.type === FIELD_TYPES.SELECT) return renderSelectField(field);
    if (field.type === FIELD_TYPES.DATE) return renderDateField(field);

    if (field.type === FIELD_TYPES.NUMBER) {
      return (
        <Grid item xs={2.4} key={field.key}>
          <FloatInput
            name={field.key}
            label={field.label}
            value={formValues[field.key] ?? ""}
            onChange={(e) => handleChange(field.key, e.target.value)}
            error={Boolean(errors[field.key])}
            disabled={fieldDisabled}
            decimalScale={field.maxDecimals ?? 4}
            maxValue={field.max}
            allowNegative={false}
          />
        </Grid>
      );
    }

    return (
      <Grid item xs={2.4} key={field.key}>
        <TextField
          fullWidth
          variant="filled"
          label={field.label}
          name={field.key}
          value={formValues[field.key] ?? ""}
          onChange={(event) => handleChange(field.key, event.target.value)}
          error={Boolean(errors[field.key])}
          disabled={fieldDisabled}
          sx={inputStyle}
          InputProps={{ disableUnderline: true }}
          inputProps={{
            ...(field.maxLength ? { maxLength: field.maxLength } : {}),
          }}
        />
      </Grid>
    );
  };

  // Required errors (ending with "is required") only highlight the field red — no summary line.
  // Descriptive errors (format, API, validation) appear in the summary box.
  const isRequiredError = (msg) => typeof msg === "string" && msg.endsWith("is required");

  const fieldErrorEntries = Object.entries(errors)
    .filter(([, msg]) => msg && !isRequiredError(msg))
    .map(([key, msg]) => ({ label: FIELD_LABEL_MAP[key] || key, msg }));
  if (identifierCheckError && !errors.identifier) {
    fieldErrorEntries.unshift({ label: "Identifier", msg: identifierCheckError });
  }
  const hasFieldErrors = fieldErrorEntries.length > 0;

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      disableEscapeKeyDown
      aria-labelledby="create-security-modal-title"
    >
      <Box
        sx={{
          ...modalStyle,
          display: "flex",
          flexDirection: "column",
          maxHeight: "95vh",
          width: "82vw",
          maxWidth: "90vw",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ ...headerStyle, flexShrink: 0 }}>
          <Typography
            id="create-security-modal-title"
            variant="h6"
            component="h2"
            sx={{ fontWeight: "bold" }}
          >
            Add Security
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: tokens.colors.fontColour }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body — pinned (identifier / price / poolFactor) first, then the rest */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          <Grid container spacing={2}>
            {PINNED_FIELDS.map(renderField)}
            {REMAINING_FIELDS.map(renderField)}
          </Grid>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            p: 2,
            borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
            backgroundColor: tokens.colors.defaultGridRowColor,
          }}
        >
          {hasFieldErrors && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: withAlpha(tokens.palette.danger, 0.133),
                border: `1px solid ${tokens.palette.danger}`,
                borderRadius: "8px",
              }}
            >
              {fieldErrorEntries.map(({ label, msg }) => (
                <Typography key={label} sx={{ color: tokens.palette.danger, fontSize: "14px", fontWeight: 500 }}>
                  {label}: {msg}
                </Typography>
              ))}
            </Box>
          )}

          {submitError && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: withAlpha(tokens.palette.danger, 0.133),
                border: `1px solid ${tokens.palette.danger}`,
                borderRadius: "8px",
              }}
            >
              <Typography sx={{ color: tokens.palette.danger, fontSize: "14px", fontWeight: 500 }}>
                {submitError}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={loading}
              sx={{
                textTransform: "none",
                fontWeight: "700",
                px: 4,
                py: 1,
                width: "150px",
                height: "46px",
                borderRadius: "8px",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || !identifierChecked}
              sx={{
                textTransform: "none",
                fontWeight: "700",
                px: 4,
                py: 1,
                width: "155px",
                height: "46px",
                borderRadius: "8px",
                backgroundColor: tokens.palette.primary,
                "&:hover": { backgroundColor: tokens.palette.primaryA80 },
                "&.Mui-disabled": {
                  backgroundColor: withAlpha(tokens.palette.primary, 0.45),
                  color: tokens.colors.white,
                  opacity: 1,
                  cursor: "not-allowed",
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Create"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreateSecurityPopup;
